<template>
    <button
        type="button"
        class="base-button"
        :disabled="disabled"
        :class="classes"
        :style="styles"
        @click="onClick"
        ref="button"
    >
        <span v-if="icon" :class="icon" :style="iconStyles" class="icon"></span>

        <span v-if="$te(text)" class="text">{{ $t(text) }}</span>
        <span v-else class="text">{{ text }}</span>

        <base-spinner v-if="loading" :size="'small'" :color="'white'"></base-spinner>

        <slot name="append">

        </slot>
    </button>
</template>

<script>
import BaseSpinner from "./BaseSpinner.vue";

const COLORS = Object.freeze([
    'default', // text color - dark gray, background color - white
    'blue', // text color - white, background color - blue
    'gray', // text color - gray, background color - light gray (initial background color - white)
    'white', // text color - blue, background color - white
    'table', // text color - dark gray, background color - light gray (initial background color - gray)
    'dark-gray', // text color - dark gray, background color - dark gray
    'orange', // text color - orange, background color - orange
    'text', // text color - gray, background color - transparent
    'green', // text color - white, background color - green
    'light-green', // text color - green, background color - green
    'delete', // text color - white, background color - red, in disabled state background color - white
    'initial-gray',
    'zoho-color',
    'zendesk-color',
    'hubspot-color',
    'red', // text color - red, background color - blue
    'purple', // text color - purple, background color - blue
    'black', // text color - black, background color - blue
    'black-text',
    'blue-text',
    'none'
])

const SIZES = Object.freeze([
    'big', // height 40px
    'default', // height 28px
    'medium', // height 26px
    'small' // height 18px
])

export default {
    name: "BaseButton",
    components: {BaseSpinner},
    props: {
        size: {
            type: String,
            default: 'default',
            validator: (value) => SIZES.includes(value)
        },
        color: {
            type: String,
            default: 'default',
            validator: (value) => COLORS.includes(value)
        },
        hoverColor: {
            type: String,
            default: null,
            validator: (value) => COLORS.includes(value) || value === null
        },
        icon: {
            type: String,
            default: ''
        },
        iconSize: {
            type: Number,
            default: 18
        },
        loading: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        text: {
            type: String,
            default: ''
        },
        radius: {
            type: String,
            default: ''
        },
        disableBorders: {
            type: Boolean,
            default: false
        },
        textOverIcon: {
            type: Boolean,
            default: false
        },
        textButton: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        classes() {
            const result = {
                'base-button-text-and-icon': this.icon && this.text && !this.textOverIcon,
                'base-button-only-icon': this.icon && !this.text,
                'base-button-loading': this.loading,
                'base-button-disable-borders': this.disableBorders || this.textButton,
                'base-button-text-over-icon': this.icon && this.text && this.textOverIcon,
                'base-button-text-button': this.textButton
            }

            if (SIZES.includes(this.size)) {
                result[`base-button-size-${this.size}`] = true
            }

            if (COLORS.includes(this.color)) {
                result[`base-button-color-${this.color}`] = true
            }

            if (this.hoverColor) {
                result[`base-button-hover-color-${this.hoverColor}`] = true
            }

            return result
        },

        styles() {
            const result = {
                borderRadius: this.radius
            }

            const hexRegex = /^#[0-9A-Fa-f]{6}$/g
            if (hexRegex.test(this.color)) {
                result.backgroundColor = this.color
            }

            return result
        },

        iconStyles() {
            const size = `${this.iconSize}px`

            return {
                fontSize: size,
                lineHeight: size
            }
        }
    },
    methods: {
        onClick(event) {
            if (this.loading) return

            this.$emit('click', event)

            if (this.$refs.button) {
                this.$refs.button.blur()
            }
        },
        click() {
            if (this.$refs.button) {
                this.$refs.button.click()
            }
        }
    }
}
</script>

<style scoped>

</style>